import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DiffEditor, useMonaco } from '@monaco-editor/react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  useTheme,
} from '@mui/material';

import { Button, Select, SelectOption } from '@components/base';
import { numberComparator } from '@utils/arrayUtils';

import useParseRevision from '../hooks/useParseRevision';
import useRawDataRevision from '../hooks/useRawDataRevision';
import { RevisionId } from '../types';

type CompareRevisionsDialogProps = {
  onClose: VoidFunction;
};

type TDiffEditor = { dispose: VoidFunction };

type Position = 0 | 1;

const CompareRevisionsDialog: FC<CompareRevisionsDialogProps> = ({ onClose }) => {
  const { revisions, revisionsToCompare } = useRawDataRevision();
  const { t } = useTranslation();
  const monaco = useMonaco();
  const theme = useTheme();

  const [revisionIds, setRevisionIds] = useState(Array.from(revisionsToCompare.values()));

  const {
    language: language0,
    parsedMetadata: parsedMetadata0,
    queryResult: { isLoading: isLoading0 },
  } = useParseRevision(revisionIds[0]);

  const {
    language: language1,
    parsedMetadata: parsedMetadata1,
    queryResult: { isLoading: isLoading1 },
  } = useParseRevision(revisionIds[1]);

  const isLoading = isLoading0 || isLoading1;

  const disposeOfDiffEditor = () =>
    monaco?.editor?.getDiffEditors()?.forEach((diffEditor: TDiffEditor) => diffEditor.dispose());

  const handleClose = () => {
    disposeOfDiffEditor();
    onClose();
  };

  const getSelectOptions = (position: Position): SelectOption[] =>
    revisions
      ?.sort((a, b) => numberComparator(b.revision, a.revision))
      .map(revision => ({
        label: t('rawMetadata.revisionNo', { count: revision.revision }),
        value: revision.revision,
        disabled:
          revision.hash === revisionIds[Number(!position)].hash ||
          (revision.hash === revisionIds[position].hash &&
            revision.revision !== revisionIds[position].revisionNumber),
      })) ?? [];

  const handleReplaceRevision = (revisionNumber: number, position: Position) => {
    const revision = revisions?.find(revision => revision.revision === revisionNumber);

    if (revision) {
      const newRevisionId: RevisionId = {
        hash: revision.hash,
        revisionNumber: revision.revision,
        playbackOptionId: revision.platformTitleId,
      };

      setRevisionIds(prev => {
        const tmp = [...prev];
        tmp[position] = newRevisionId;
        return tmp;
      });
    }
  };

  const getSource = (language?: string, parsedMetadata?: string) => {
    switch (language) {
      case 'json':
      default:
        return JSON.stringify(parsedMetadata, null, 2);
      case 'xml':
        return parsedMetadata;
    }
  };

  return (
    <Dialog fullWidth maxWidth={false} onClose={handleClose} open>
      <DialogTitle>{t('rawMetadata.compare')}</DialogTitle>

      <Box component={DialogContent} minHeight='76vh'>
        <Grid container spacing={1}>
          <Grid size={{ xs: 12, md: 5.5, lg: 2 }}>
            <Select
              fullWidth
              label={t('rawMetadata.compareRevisions.select')}
              onChange={({ target: { value } }) => handleReplaceRevision(Number(value), 0)}
              options={getSelectOptions(0)}
              value={revisionIds[0].revisionNumber}
            />
          </Grid>

          <Grid offset={{ md: 0.3, lg: 3.875 }} size={{ xs: 12, md: 5.5, lg: 2 }}>
            <Select
              fullWidth
              label={t('rawMetadata.compareRevisions.select')}
              onChange={({ target: { value } }) => handleReplaceRevision(Number(value), 1)}
              options={getSelectOptions(1)}
              value={revisionIds[1].revisionNumber}
            />
          </Grid>

          <Grid size={12}>
            <DiffEditor
              height='74vh'
              language={language1}
              loading={isLoading}
              modified={getSource(language1, parsedMetadata1)}
              options={{ renderSideBySide: true, readOnly: true }}
              original={getSource(language0, parsedMetadata0)}
              theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'light'}
            />
          </Grid>
        </Grid>
      </Box>

      <DialogActions>
        <Button onClick={handleClose}>{t('close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompareRevisionsDialog;
